import { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, Paper, InputAdornment, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { useChangepasswordMutation } from './services/authenticationApi';
import { isLoginFunc } from '../../store/slices/login';
import { clearPermissionCache } from '../../hooks/usePermission';
import { globalVal } from '../../utils/global-values';
import CircularProgress from '@mui/material/CircularProgress';

const GradientBackground = styled(Box)(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
    clipPath: 'polygon(0 0, 51% 0, 39% 100%, 0% 100%)',
    '@media (max-width: 900px)': {
        clipPath: 'none'
    }
}));

interface ResetPasswordFormData {
    oldPassword: string;
    newPassword: string;
    confirmNewPassword: string;
    ip: string;
}

const ChangePassword = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [changePassword, { isLoading }] = useChangepasswordMutation();
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const requestInProgressRef = useRef(false);

    const { register, handleSubmit, formState: { errors }, watch } = useForm<ResetPasswordFormData>();
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowConfirmPassword] = useState(false);

    const handleChangePassword = async (data: ResetPasswordFormData) => {
        if (requestInProgressRef.current || isLoading || isSubmitting) return;
        
        requestInProgressRef.current = true;
        setIsSubmitting(true);
        
        try {
            const response = await changePassword({
                ip: "1.1.1.1",
                oldPassword: data.oldPassword,
                newPassword: data.newPassword,
                confirmPassword: data.confirmNewPassword,
            }).unwrap();

            if (response.success) {
                enqueueSnackbar(response.message, { variant: 'success' });
                setIsFadingOut(true);
                setTimeout(() => {
                    clearPermissionCache();
                    globalVal.isLoggedOut = true;
                    dispatch(isLoginFunc(false));
                    localStorage.clear();
                    navigate('/login', { replace: true });
                }, 400); // 400ms fade süresi
                // Başarılı durumda state'leri sıfırlama - sayfa reload olacak
                return;
            }
        } catch (error: any) {
            // Hata durumunda state'leri sıfırla
            setIsSubmitting(false);
            requestInProgressRef.current = false;
        }
    };

    return (
        <Box className={isFadingOut ? 'fade-out' : ''} sx={{
            position: 'relative',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: "100vh",
            overflowX: 'hidden'
        }}>
            <IconButton
                onClick={() => navigate(-1)}
                sx={{
                    position: 'absolute',
                    top: { xs: '80px', md: '20px' },
                    left: { xs: '20px', md: '40px' },
                    zIndex: 100,
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#FFFFFF',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }}
            >
                <ArrowBack />
            </IconButton>
            <Box
                sx={{
                    width: { xs: '80%', xl: '51.4%' },
                    '@media (max-width: 900px)': {
                        textAlign: 'center',
                        paddingTop: '70px'
                    },
                    marginBottom: { xs: '15px', md: '20px' }
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 800,
                        fontSize: { xs: '20px', loginxs: '30px', xl: '33px' },
                        color: '#FFFFFF !important',
                        zIndex: 50,
                        position: "relative",
                    }}
                >
                    AHLATCI
                </Typography>
                <Typography
                    sx={{
                        fontWeight: 400,
                        fontSize: { xs: '14px', lg: '20px', '4xl': '27px' },
                        color: '#FFFFFF !important',
                        zIndex: 50,
                        position: "relative",
                        margin: "10px 0",
                    }}
                >
                    Ödeme ve Elektronik Para Hizmetleri A.Ş.
                </Typography>
            </Box>
            <Paper
                component="form"
                onSubmit={handleSubmit(handleChangePassword)}
                elevation={3}
                sx={{
                    width: { xs: '80%', xl: '51.4%' },
                    maxWidth: '984px',
                    maxHeight: { xs: 'calc(100vh - 100px)', md: 'calc(100vh - 120px)' },
                    overflow: 'auto',
                    padding: { xs: '0 0 20px 0', md: 5 },
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                    borderRadius: '35px',
                    border: '1.2px solid rgba(0, 0, 0, 0.1)',
                    zIndex: 50,
                    boxSizing: 'border-box'
                }}
            >
                <Box sx={{ width: { xs: '60%', md: '41%' }, p: 2.5, color: 'white' }}>
                    <Box
                        component="img"
                        src={`${process.env.PUBLIC_URL}/assets/images/logo/newlogo.png`}
                        alt="Logo"
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </Box>

                <Box sx={{
                    width: { xs: '100%', md: '59%' },
                    bgcolor: { xs: 'white', md: 'transparent' },
                    p: { xs: '20px', md: 0 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: { xs: 2, md: 3 },
                    maxHeight: '100%',
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '16px', md: '22px' }, marginBottom: { xs: 1, md: 0 } }}>
                        Şifrenizi Değiştiriniz
                    </Typography>
                    <Box sx={{ width: '80%', display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
                        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                            <span style={{ color: 'red' }}>*</span> Eski Şifreniz
                        </Typography>
                        <TextField
                            fullWidth
                            label="Eski Şifreniz"
                            type={showPassword ? 'text' : 'password'}
                            {...register('oldPassword', {
                                required: 'Eski şifre gerekli',
                            })}
                            error={!!errors.oldPassword}
                            helperText={errors.oldPassword?.message}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ mb: { xs: 1, md: 1.5 } }}
                        />

                        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                            <span style={{ color: 'red' }}>*</span> Yeni Şifreniz
                        </Typography>
                        <TextField
                            fullWidth
                            label="Yeni Şifreniz"
                            type={showNewPassword ? 'text' : 'password'}
                            {...register('newPassword', {
                                required: 'Yeni şifre gerekli',
                                minLength: {
                                    value: 6,
                                    message: 'Yeni şifre en az 6 karakter olmalıdır'
                                }
                            })}
                            error={!!errors.newPassword}
                            helperText={errors.newPassword?.message}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirmPassword(!showNewPassword)} edge="end">
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ mb: { xs: 1, md: 1.5 } }}
                        />

                        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                            <span style={{ color: 'red' }}>*</span> Tekrar Yeni Şifreniz
                        </Typography>
                        <TextField
                            fullWidth
                            label="Tekrar Yeni Şifreniz"
                            type={showNewPassword ? 'text' : 'password'}
                            {...register('confirmNewPassword', {
                                required: 'Yeni şifre tekrarı gerekli',
                                validate: (val: string) => {
                                    if (watch('newPassword') !== val) {
                                        return "Girilen şifreler eşleşmemektedir.";
                                    }
                                }
                            })}
                            error={!!errors.confirmNewPassword}
                            helperText={errors.confirmNewPassword?.message}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirmPassword(!showNewPassword)} edge="end">
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ mb: { xs: 2, md: 2.5 } }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            disabled={isLoading || isSubmitting}
                            sx={{
                                height: '52px',
                                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                borderColor: '#1e3c72',
                                borderRadius: '12px',
                                color: 'white',
                                fontSize: '15px',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: '0 4px 16px rgba(30, 60, 114, 0.25)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #1a3569 0%, #25478a 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 24px rgba(30, 60, 114, 0.35)'
                                },
                                '&:disabled': {
                                    background: 'rgba(30, 60, 114, 0.3)',
                                    color: 'rgba(255, 255, 255, 0.6)'
                                },
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            {(isLoading || isSubmitting) ? <CircularProgress size={24} color="inherit" /> : 'Gönder'}
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <GradientBackground />

            <style>{`
                .fade-out {
                    animation: fadeOutChangePassword 0.4s forwards;
                }
                @keyframes fadeOutChangePassword {
                    from { opacity: 1; }
                    to { opacity: 0; }
                }
            `}</style>
        </Box>
    );
};

export default ChangePassword;
